/**
 * @typedef {Object} BoxResponseDto
 * @property {number} boxId
 * @property {string} boxCode
 * @property {string} boxName
 * @property {number} boxTypeId
 * @property {string} boxTypeCode
 * @property {string} boxTypeName
 * @property {number} [price]
 * @property {number} [length]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [dimensionUnit]
 * @property {number} quantityOnHand
 * @property {boolean} isActive
 */

/**
 * @typedef {Object} BoxTypeDto
 * @property {number} boxTypeId
 * @property {string} boxTypeCode
 * @property {string} boxTypeName
 */

const BASE_PATH = 'v1/Box';

export default function (instance) {
  return {
    /**
     * Get all box types (Cardboard/Plastic/Bag/etc.)
     * @returns {Promise<{data: BoxTypeDto[]}>}
     */
    getTypes() {
      return instance.get(`${BASE_PATH}/types`);
    },

    /**
     * Get all boxes
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @returns {Promise<{data: BoxResponseDto[]}>}
     */
    getAll(params = {}) {
      return instance.get(BASE_PATH, { params });
    },

    /**
     * Get box by ID
     * @param {number} id
     * @returns {Promise<{data: BoxResponseDto}>}
     */
    getById(id) {
      return instance.get(`${BASE_PATH}/${id}`);
    },

    /**
     * Create a new box
     * @param {Object} payload
     * @returns {Promise<{data: BoxResponseDto}>}
     */
    create(payload) {
      return instance.post(BASE_PATH, payload);
    },

    /**
     * Update a box
     * @param {number} id
     * @param {Object} payload
     * @returns {Promise<{data: BoxResponseDto}>}
     */
    update(id, payload) {
      return instance.put(`${BASE_PATH}/${id}`, payload);
    },

    /**
     * Delete a box (soft delete)
     * @param {number} id
     */
    delete(id) {
      return instance.delete(`${BASE_PATH}/${id}`);
    },

    /**
     * Get a box's current stock per warehouse
     * @param {number} id
     * @returns {Promise<{data: Array<{warehouseId: number, warehouseName: string, quantityOnHand: number, quantityReserved: number, quantityAvailable: number}>}>}
     */
    getInventory(id) {
      return instance.get(`${BASE_PATH}/${id}/inventory`);
    },

    /**
     * Manually adjust a box's stock at a warehouse
     * @param {number} id
     * @param {Object} payload
     * @param {number} payload.warehouseId
     * @param {number} payload.quantityDelta
     * @param {string} [payload.notes]
     */
    adjustInventory(id, payload) {
      return instance.post(`${BASE_PATH}/${id}/inventory/adjust`, payload);
    }
  };
}
